import {useEffect, useRef, useState} from "react";
import {DocumentNode} from "graphql";
import {useQuery} from "@apollo/client";
import {useFlags} from "launchdarkly-react-client-sdk";
import {type GraphQLQuery} from "@aws-amplify/api-graphql";
import {features} from "../../features";
import {useAmplifyClients} from "../AmplifyClientProvider";
import {withRetry} from "./retry";
import {
  AmplifyClientKey,
  GraphqlResult,
  GraphqlVariables,
  toError
} from "./types";

interface UseGraphqlQueryOptions<TData, TVariables extends GraphqlVariables> {
  /** Apollo DocumentNode — required during migration, removed after Apollo cleanup */
  document: DocumentNode;
  /** Amplify query string */
  amplifyQuery: string;
  /** Variables for the query */
  variables?: TVariables;
  /** Which Amplify client to use (defaults to "appsyncBff") */
  amplifyClient?: AmplifyClientKey;
  /** Apollo context (e.g., { endpoint: Endpoints.APPSYNC }) */
  context?: Record<string, unknown>;
  /** Skip execution */
  skip?: boolean;
}

/**
 * Framework-agnostic query hook returning { data, loading, error }.
 * Routes to Apollo or Amplify based on the migration feature flag.
 */
export function useGraphqlQuery<
  TData = unknown,
  TVariables extends GraphqlVariables = GraphqlVariables
>(options: UseGraphqlQueryOptions<TData, TVariables>): GraphqlResult<TData> {
  const flags = useFlags();
  const useAmplify =
    flags[features.dev.amplifyClient.useAmplifyForAllOperations];

  // === Apollo path ===
  const apolloResult = useQuery<TData, TVariables>(options.document, {
    variables: options.variables as TVariables,
    context: options.context,
    skip: useAmplify || options.skip
  });

  // === Amplify path ===
  const clients = useAmplifyClients();
  const client = clients?.[options.amplifyClient ?? "appsyncBff"] ?? null;
  const [amplifyData, setAmplifyData] = useState<TData | undefined>(undefined);
  const [amplifyLoading, setAmplifyLoading] = useState(false);
  const [amplifyError, setAmplifyError] = useState<Error | undefined>(
    undefined
  );
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!useAmplify || options.skip || !client) return;

    cancelledRef.current = false;
    setAmplifyLoading(true);
    setAmplifyError(undefined);

    const execute = async () => {
      try {
        const result = await withRetry(() =>
          client.graphql<GraphQLQuery<TData>>({
            query: options.amplifyQuery,

            variables: options.variables as any
          })
        );
        const data = result.data as TData;

        if (!cancelledRef.current) {
          setAmplifyData(data);
          setAmplifyLoading(false);
        }
      } catch (err) {
        if (!cancelledRef.current) {
          setAmplifyError(toError(err));
          setAmplifyLoading(false);
        }
      }
    };

    execute();
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAmplify, options.skip, client, JSON.stringify(options.variables)]);

  if (useAmplify) {
    return {
      data: amplifyData,
      loading: amplifyLoading,
      error: amplifyError
    };
  }

  return {
    data: apolloResult.data,
    loading: apolloResult.loading,
    error: apolloResult.error
  };
}
